import { useCreatePostModalInvalidations } from "@circle-react/components/Spaces/ImageSpace/hooks/useCreatePostModalInvalidations";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePostDetails } from "@circle-react/contexts/postsDetailsContext";
import { Modal } from "@circleco/compass/components/Modal";
import { useNavigate } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import type { ImagePostApiResponse } from "@/react/api/imagePostApi";
import { useCurrentPostHelpers } from "@/react/components/PostsV3/useCurrentPostHelpers";
import { useImagePostMutation } from "@/react/components/Spaces/ImageSpace/ImagePostModalCompass/CreatePost/hooks/useImagePostMutation";
import { afterSaveRedirectPath } from "@/react/helpers/postHelpers";
import {
  useExtraLargeScreenMediaQuery,
  useTailwindLgScreenMediaQuery,
} from "@/react/hooks/useMediaQuery";

import { getCreatePostModalSize } from "../getCreatePostModalSize";
import { Carousel } from "./Carousel";
import { useCreatePostActions } from "./CreatePostActionsComponent";
import { defaultValues, requireImagePostFormPost } from "./helpers";
import { ImagePostForm } from "./ImagePostForm";
import { ScheduledPostBox } from "./ImagePostForm/ScheduledPostBox";
import type { CreatePostProps, ImagePostFormValues } from "./types";

interface CreatePostComponentProps extends CreatePostProps {
  open: boolean;
}

const FORM_ID = "image-post-modal-create-post-form";

export const CreatePostComponent = ({
  open,
  onClose,
  onPostChange,
  shouldSkipRedirectAfterSubmit,
}: CreatePostComponentProps) => {
  const post = requireImagePostFormPost(useCurrentPostHelpers().post);
  const { postDetails } = usePostDetails(post.id);
  const { data: space } = useCurrentSpaceContext();
  const invalidateQueries = useCreatePostModalInvalidations();
  const isXlScreen = useExtraLargeScreenMediaQuery();
  const isLgScreen = useTailwindLgScreenMediaQuery();
  const formMethods = useForm<ImagePostFormValues>({
    defaultValues: defaultValues(post),
    values: postDetails
      ? {
          ...defaultValues(post),
          meta_tag_attributes: postDetails.meta_tag_attributes ?? {},
        }
      : undefined,
    // Metadata arrives after the image and caption; preserve edits made meanwhile.
    resetOptions: { keepDirtyValues: true, keepDirty: true },
  });

  const modalSize = getCreatePostModalSize({ isLgScreen, isXlScreen });
  const navigate = useNavigate();
  const onSuccess = async (response: ImagePostApiResponse) => {
    onPostChange(response);
    await invalidateQueries(response.id);
    onClose(true);
    if (!shouldSkipRedirectAfterSubmit) {
      void navigate({ href: afterSaveRedirectPath({ post: response }) });
    }
  };
  const { onSubmit } = useImagePostMutation({ space, post, onSuccess });
  const { footerProps, supportContent } = useCreatePostActions({
    post,
    onCancel: () => onClose(false),
    watch: formMethods.watch,
    setValue: formMethods.setValue,
  });

  return (
    <Modal.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose(false);
      }}
      size={modalSize}
    >
      <Modal.Content>
        {supportContent}
        <Modal.Header
          title={
            post.id
              ? t("image_space.post_modal.steps.create_post.edit_title")
              : t("image_space.post_modal.steps.create_post.create_title")
          }
        />
        <Modal.Body customLayout={isLgScreen}>
          <FormProvider {...formMethods}>
            <form
              id={FORM_ID}
              className="flex min-h-0 flex-1 flex-col gap-3 lg:gap-0"
              onSubmit={formMethods.handleSubmit(onSubmit)}
            >
              {!isLgScreen && space && (
                <ScheduledPostBox post={post} space={space} />
              )}
              <div className="grid min-h-0 gap-6 lg:h-[455px] lg:grid-cols-[minmax(0,35fr)_minmax(0,75fr)] xl:h-[568px] xl:grid-cols-[minmax(0,30fr)_minmax(0,70fr)]">
                <div className="w-full min-w-0 pb-4 lg:order-2 lg:flex lg:min-h-0 lg:pb-0">
                  <Carousel />
                </div>
                <div className="w-full min-w-0 pb-4 lg:order-1 lg:min-h-0 lg:overflow-y-auto lg:pb-0">
                  <ImagePostForm
                    post={post}
                    space={space}
                    showScheduledPostBox={isLgScreen}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Modal.Body>
        <Modal.Footer
          {...footerProps}
          primaryAction={{ ...footerProps.primaryAction, form: FORM_ID }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
